import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { CADASTRAL_REFERENCE_QUERY_PARAMETER } from './ram-layer.config';

interface GeoJsonFeatureInfo {
  features?: GeoJsonFeature[];
}

interface GeoJsonFeature {
  id?: string | number;
  properties?: Record<string, unknown>;
}

export interface RamFeatureInfoItem {
  id?: string | number;
  properties: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class RamWmsService {
  createAuthenticatedImageLoadFunction(
    getAuthorizationHeaders: () => Record<string, string>,
    onUnauthorized: () => void
  ): (image: any, src: string) => void {
    return async (image: any, src: string): Promise<void> => {
      const imageElement = image.getImage();
      const headers = getAuthorizationHeaders();

      if (!headers['Authorization']) {
        imageElement.src = src;
        return;
      }

      try {
        const response = await fetch(src, { headers });
        if (!response.ok) {
          if (response.status === 401) {
            onUnauthorized();
          }

          throw new Error(`HTTP ${response.status}`);
        }

        const objectUrl = URL.createObjectURL(await response.blob());
        imageElement.onload = (): void => URL.revokeObjectURL(objectUrl);
        imageElement.onerror = (): void => URL.revokeObjectURL(objectUrl);
        imageElement.src = objectUrl;
      } catch {
        imageElement.src = '';
      }
    };
  }

  getProxyWmsUrl(layerKey: string): string {
    return `${environment.apiWmsUrl}/${encodeURIComponent(layerKey)}`;
  }

  getControlledFeatureInfoUrl(rawInfoUrl: string | undefined, layerKey: string): string | undefined {
    if (!rawInfoUrl) {
      return undefined;
    }

    const proxyUrl = new URL(this.getProxyWmsUrl(layerKey), window.location.origin);
    const candidateUrl = new URL(rawInfoUrl, window.location.origin);

    if (candidateUrl.origin !== proxyUrl.origin || candidateUrl.pathname !== proxyUrl.pathname) {
      return undefined;
    }

    const allowedParameters = [
      'BBOX',
      'CRS',
      'HEIGHT',
      'I',
      'J',
      'SERVICE',
      'SRS',
      'VERSION',
      'WIDTH',
      'X',
      'Y',
      CADASTRAL_REFERENCE_QUERY_PARAMETER
    ];
    const controlledUrl = new URL(proxyUrl.toString());

    allowedParameters.forEach((parameter) => {
      const value = candidateUrl.searchParams.get(parameter);
      if (value !== null) {
        controlledUrl.searchParams.set(parameter, value);
      }
    });

    controlledUrl.searchParams.set('SERVICE', 'WMS');
    controlledUrl.searchParams.set('REQUEST', 'GetFeatureInfo');
    controlledUrl.searchParams.set('INFO_FORMAT', 'application/json');

    return controlledUrl.toString();
  }

  getFeatureProperties(featureInfo: GeoJsonFeatureInfo): Record<string, unknown>[] {
    return this.getFeatureInfoItems(featureInfo).map((feature) => feature.properties);
  }

  getFeatureInfoItems(featureInfo: unknown): RamFeatureInfoItem[] {
    if (!featureInfo || typeof featureInfo !== 'object') {
      return [];
    }

    const features = (featureInfo as GeoJsonFeatureInfo).features;
    if (!Array.isArray(features)) {
      return [];
    }

    return features.map((feature) => ({
      id: feature.id,
      properties: feature.properties ?? {}
    }));
  }
}
